import { Injectable } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { environment } from '../../environments/environment';
import { filter, firstValueFrom, take, timeout } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class BackendAuthService {
  constructor(
    private msalService: MsalService,
    private msalBroadcast: MsalBroadcastService
  ) {}

  private interactiveAcquireInProgress = false;

  private resolveConfiguredRedirectUri(): string {
    const configured = (environment.msalConfig?.auth?.redirectUri ?? '').toString().trim();
    if (!configured) {
      return '';
    }

    try {
      // MSAL allows relative redirect URIs (e.g., '/'). Normalize so logs are actionable.
      return new URL(configured, window.location.origin).toString();
    } catch {
      return configured;
    }
  }

  private logHelpfulPostRequestFailed(error: any) {
    const origin = (() => {
      try {
        return window.location.origin;
      } catch {
        return '';
      }
    })();

    const redirectUri = this.resolveConfiguredRedirectUri();

    console.error(
      'MSAL token request failed (post_request_failed). This is commonly caused by SPA redirect URI / CORS misconfiguration in Azure App Registration.',
      {
        origin,
        configuredRedirectUri: environment.msalConfig?.auth?.redirectUri,
        resolvedRedirectUri: redirectUri,
        authority: environment.msalConfig?.auth?.authority,
        clientId: environment.msalConfig?.auth?.clientId,
        error
      }
    );
  }

  private async delay(ms: number): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, ms));
  }

  private async waitForNoMsalInteraction(maxWaitMs: number = 10000): Promise<void> {
    try {
      await firstValueFrom(
        this.msalBroadcast.inProgress$.pipe(
          filter((status) => status === InteractionStatus.None),
          take(1),
          timeout({ first: maxWaitMs })
        )
      );
    } catch {
      // ignore
    }
  }

  private isMsalInteractionFlagSet(): boolean {
    const key = 'msal.interaction.status';
    const raw =
      (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(key) : null) ||
      (typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null);
    if (!raw) return false;
    try {
      const parsed = JSON.parse(raw);
      return !!parsed?.clientId;
    } catch {
      // If it exists but is malformed, treat it as set (safer).
      return true;
    }
  }

  async getBackendToken(): Promise<string> {
    await this.waitForNoMsalInteraction();

    const accounts = this.msalService.instance.getAllAccounts();

    if (accounts.length === 0) {
      throw new Error('No active account found');
    }

    const account = this.msalService.instance.getActiveAccount() ?? accounts[0];

    const backendTokenRequest = {
      scopes: [environment.msalConfig.backendApiScope],
      account
    };

    try {
      const response = await this.msalService.instance.acquireTokenSilent(backendTokenRequest);
      return response.accessToken;
    } catch (error) {
      const errorCode = (error as any)?.errorCode;

      // If AAD says interaction is required (consent, login, MFA, etc), start an interactive
      // flow once for the backend scope.
      if (
        error instanceof InteractionRequiredAuthError ||
        errorCode === 'interaction_required' ||
        errorCode === 'refresh_token_expired'
      ) {
        if (!this.interactiveAcquireInProgress) {
          this.interactiveAcquireInProgress = true;

          // If an MSAL interaction is already flagged in storage, do not attempt to start
          // another redirect (it will throw interaction_in_progress).
          if (this.isMsalInteractionFlagSet()) {
            this.interactiveAcquireInProgress = false;
            throw error;
          }

          await this.waitForNoMsalInteraction(5000);
          try {
            await this.msalService.instance.acquireTokenRedirect({
              scopes: [environment.msalConfig.backendApiScope],
              account
            });
          } catch (redirectError) {
            if ((redirectError as any)?.errorCode === 'interaction_in_progress') {
              this.interactiveAcquireInProgress = false;
              throw error;
            }
            throw redirectError;
          }
        }
        throw error;
      }

      // MSAL uses BrowserAuthError 'post_request_failed' when the token POST is blocked by CORS
      // (often because the current origin isn't registered as a SPA redirect URI in Entra ID),
      // or when the network stack is temporarily unavailable.
      if (errorCode === 'post_request_failed') {
        this.logHelpfulPostRequestFailed(error);
        // One retry helps with transient socket/network failures.
        await this.delay(250);
        const retryResponse = await this.msalService.instance.acquireTokenSilent(backendTokenRequest);
        return retryResponse.accessToken;
      }

      // Silent refresh via hidden iframe timed out. Most common causes:
      // - 3rd-party cookies blocked (Safari ITP / Chrome tracking protection)
      // - corporate proxy / network intermittently blocking login.microsoftonline.com
      // Retrying once covers transient slowness; otherwise fall back to interactive.
      if (errorCode === 'monitor_window_timeout') {
        try {
          await this.delay(250);
          const retryResponse = await this.msalService.instance.acquireTokenSilent(backendTokenRequest);
          return retryResponse.accessToken;
        } catch (retryError) {
          if (!this.interactiveAcquireInProgress) {
            this.interactiveAcquireInProgress = true;
            await this.msalService.instance.acquireTokenRedirect({
              scopes: [environment.msalConfig.backendApiScope],
              account
            });
          }
          throw retryError;
        }
      }

      throw error;
    }
  }
}
